import { AnyAction } from "redux";
import { action } from "typesafe-actions";
import { IDndNumberData, IDndNumberResponse } from "./dnd-number.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import {
    showMessage,
} from "../messages/messagesActions";

// List actions
export const FETCH_DND_NUMBER_DATA_PROGRESS = "FETCH_DND_NUMBER_DATA_PROGRESS";
export const FETCH_DND_NUMBER_DATA_SUCCESS = "FETCH_DND_NUMBER_DATA_SUCCESS";
export const FETCH_DND_NUMBER_DATA_FAILURE = "FETCH_DND_NUMBER_DATA_FAILURE";

export const fetchDndNumberDataProgress = () => action(FETCH_DND_NUMBER_DATA_PROGRESS);

export const fetchDndNumberDataSuccess = (
    data: IDndNumberData[],
    totalRecords: number,
    currentRecords: number,
    currentPage: number,
    itemsPerPage: number,
    totalPages: number
) => action(FETCH_DND_NUMBER_DATA_SUCCESS, { 
    data, 
    totalRecords, 
    currentRecords, 
    currentPage, 
    itemsPerPage, 
    totalPages 
});

export const fetchDndNumberDataFailure = (error: string) =>
    action(FETCH_DND_NUMBER_DATA_FAILURE, error);

export const fetchDndNumberDataAsync =
    (
        forReport: boolean = false,
        query: {
            number?: string,
            name?: string,
            status?: string,
            pageNo?: number,
            itemsPerPage?: number,
            from_date?: string,
            to_date?: string,
        }
    ): ThunkAction<Promise<IDndNumberData[] | void>, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(fetchDndNumberDataProgress());
                let url = "";
                if(forReport === false){
                    url += `pageNo=${query.pageNo}&itemsPerPage=${query.itemsPerPage}&`;
                }
                if (query.number) url += `number=${query.number}&`;
                if (query.name) url += `name=${query.name}&`;
                if (query.status) url += `status=${query.status}&`;
                if (query.pageNo !== undefined) url += `pageNo=${query.pageNo}&`;
                if (query.itemsPerPage !== undefined) url += `itemsPerPage=${query.itemsPerPage}&`;
                if (query.from_date) url += `from_date=${query.from_date}&`;
                if (query.to_date) url += `to_date=${query.to_date}&`;
                const res = await api.get(`get-dnd-number?${url}`);
                const response: IDndNumberResponse = res.data;
                const data: IDndNumberData[] = response.result;
                const { totalRecords, currentRecords, currentPage, itemsPerPage, totalPages } = response;

                if(forReport){
                    return data;
                }

                dispatch(
                    showMessage({
                        message: "DND Number Data fetched successfully",
                        type: "success",
                        displayAs: "snackbar",
                    })
                );
                dispatch(fetchDndNumberDataSuccess(data, totalRecords, currentRecords, currentPage, itemsPerPage, totalPages));
            } catch (error: any) {
                dispatch(fetchDndNumberDataFailure(error.message));
                dispatch(
                    showMessage({
                        message: error.response?.data?.message || error.message,
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

// Single record actions
export const FETCH_SINGLE_DND_NUMBER_DATA_PROGRESS = "FETCH_SINGLE_DND_NUMBER_DATA_PROGRESS";
export const FETCH_SINGLE_DND_NUMBER_DATA_SUCCESS = "FETCH_SINGLE_DND_NUMBER_DATA_SUCCESS";
export const FETCH_SINGLE_DND_NUMBER_DATA_FAILURE = "FETCH_SINGLE_DND_NUMBER_DATA_FAILURE";

export const fetchSingleDndNumberDataProgress = () =>
    action(FETCH_SINGLE_DND_NUMBER_DATA_PROGRESS);

export const fetchSingleDndNumberDataSuccess = (data: IDndNumberData) =>
    action(FETCH_SINGLE_DND_NUMBER_DATA_SUCCESS, data);

export const fetchSingleDndNumberDataFailure = (error: string) =>
    action(FETCH_SINGLE_DND_NUMBER_DATA_FAILURE, error);

export const fetchSingleDndNumberDataAsync =
    (id: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(fetchSingleDndNumberDataProgress());
                const res = await api.get(`get-dnd-number/${id}`);
                const data: IDndNumberData = res.data.result;
                dispatch(
                    showMessage({
                        message: "DND Number Data fetched successfully",
                        type: "success",
                        displayAs: "snackbar",
                    })
                );
                dispatch(fetchSingleDndNumberDataSuccess(data));
            } catch (error: any) {
                dispatch(fetchSingleDndNumberDataFailure(error.message));
                dispatch(
                    showMessage({
                        message: error.response?.data?.message || error.message,
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

// Insert DND Number actions
export const INSERT_DND_NUMBER_DATA_PROGRESS = "INSERT_DND_NUMBER_DATA_PROGRESS";
export const INSERT_DND_NUMBER_DATA_SUCCESS = "INSERT_DND_NUMBER_DATA_SUCCESS";
export const INSERT_DND_NUMBER_DATA_FAILURE = "INSERT_DND_NUMBER_DATA_FAILURE";

export const insertDndNumberDataProgress = () => action(INSERT_DND_NUMBER_DATA_PROGRESS);

export const insertDndNumberDataSuccess = (data: IDndNumberData) =>
    action(INSERT_DND_NUMBER_DATA_SUCCESS, data);

export const insertDndNumberDataFailure = (error: string) =>
    action(INSERT_DND_NUMBER_DATA_FAILURE, error);

export const insertDndNumberDataAsync =
    (payload: { number: string; name: string; comment: string }): ThunkAction<Promise<void>, IStoreState, {}, AnyAction> =>
        async (dispatch) => {
            try {
                dispatch(insertDndNumberDataProgress());
                const res = await api.post(`insert-dnd-number`, payload);
                const data: IDndNumberData = res.data.result;
                dispatch(
                    showMessage({
                        message: "DND Number inserted successfully",
                        type: "success",
                        displayAs: "snackbar",
                    })
                );
                dispatch(insertDndNumberDataSuccess(data));
            } catch (error: any) {
                dispatch(insertDndNumberDataFailure(error.message));
                dispatch(
                    showMessage({
                        message: error.response?.data?.message || error.message,
                        type: "error",
                        displayAs: "snackbar",
                    })
                );
            }
        };

export const CLEAR_DND_NUMBER_DATA = "CLEAR_DND_NUMBER_DATA";
export const CLEAR_DND_NUMBER_DATA_STATE = "CLEAR_DND_NUMBER_DATA_STATE";
export const clearDndNumberData = () => action(CLEAR_DND_NUMBER_DATA);
export const clearDndNumberDataState = () => action(CLEAR_DND_NUMBER_DATA_STATE);
